import { CommonModule } from "@angular/common";
import { Component, inject, Inject, OnInit } from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { EventSpace } from "@models/space";
import { SnackBarService } from "@services/snack-bar.service";
import { DatePickerModule } from "primeng/datepicker";
import { TextareaModule } from "primeng/textarea";
import { ButtonComponent } from "../../../../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../../../../shared/components/dialog-header/dialog-header.component";
import { EventTicketBatch } from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { DatepickerMaskDirective } from "../../../../../../shared/directives/datepicker-mask/datepicker-mask.directive";
import {
  buildSpaceEditModalForm,
  SpaceEditModalForm,
} from "./space-edit-modal.form";

export interface SpaceEditDialogData {
  space?: EventSpace;
  ticketBatches: EventTicketBatch[];
}

@Component({
  selector: "app-space-edit-modal",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    DatePickerModule,
    DatepickerMaskDirective,
    TextareaModule,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./space-edit-modal.component.html",
  styleUrl: "./space-edit-modal.component.scss",
})
export class SpaceEditModalComponent implements OnInit {
  space?: EventSpace;
  spaceForm!: SpaceEditModalForm;
  dateNow = new Date();
  private confirmed = false;

  private readonly dialogRef = inject(MatDialogRef<SpaceEditModalComponent>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly snackbarService = inject(SnackBarService);

  ticketBatches: EventTicketBatch[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: SpaceEditDialogData) {
    this.space = data.space;
    this.ticketBatches = data.ticketBatches ?? [];
  }

  ngOnInit(): void {
    this.spaceForm = buildSpaceEditModalForm({
      formBuilder: this.formBuilder,
      space: this.space,
      ticketBatches: this.ticketBatches,
    });
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  saveSpace(): void {
    if (this.confirmed) return;
    if (!this.spaceForm.valid) {
      this.spaceForm.markAllAsTouched();
      this.snackbarService.openSnackBar(
        "Formulário possui dados inválidos.",
        "Fechar",
        "error",
      );
      return;
    }

    const formValues = this.spaceForm.getRawValue();

    const savedSpace: EventSpace = {
      ...(this.space ?? ({} as EventSpace)),
      title: formValues.title,
      description: formValues.description,
      quantity: Number(formValues.quantity),
      price: Number(formValues.price),
      consumption: Number(formValues.consumption),
      sale_start: formValues.saleStart!.toISOString(),
      sale_end: formValues.saleEnd!.toISOString(),
      availability: this.space?.availability ?? "general public",
    };

    // This only confirms the draft; the event form reports persistence results.
    this.confirmed = true;
    this.dialogRef.close(savedSpace);
  }

  getTaxString(): string {
    const price = Number(this.spaceForm.getRawValue().price) || 0;
    const consumption = Number(this.spaceForm.getRawValue().consumption) || 0;
    const tax = price * 0.1;
    return `Ingresso R$${price} + Consumação R$${consumption} + Taxa R$${tax}`;
  }

  getTaxValue(): number {
    const price = Number(this.spaceForm.getRawValue().price) || 0;
    return price * 0.1;
  }

  getFinalPriceValue(): number {
    const price = Number(this.spaceForm.getRawValue().price) || 0;
    const consumption = Number(this.spaceForm.getRawValue().consumption) || 0;
    return price + consumption + price * 0.1;
  }
}
